import * as React from "react";
import { connect, useSelector } from "react-redux";
import { push } from "connected-react-router";
import cloneDeep from "lodash/cloneDeep";
import {
  Box,
  Typography,
  ButtonGroup,
  Button,
  Checkbox,
  FormControlLabel,
  Tooltip
} from "@mui/material";
import InfoIcon from "@mui/icons-material/Info";
import Close from "@mui/icons-material/Close";
import queryString from "query-string";
import { round } from "mathjs";
import produce from "immer";
import {
  serviceProviderActions,
  getServices,
  createServices,
  updateServices,
  getService,
  updateServiceUnderPackage
} from "../../actions";
import { getBatchLabTests } from "../../actions/assessments";
import * as NotificationActions from "../../actions/notification";
import { removeGeneralErrorByKey } from "../../actions/error";
import { t, tl } from "../../components/translate";
import Panel from "../../components/Panel";
import CommonServiceFields from "./CommonServiceFields";
import RateRow from "./RateRow";
import Footer from "./Footer";
import {
  spFullNameSelector,
  spFromIdSelector,
  serviceProvidersSortedSelector
} from "../../reducers/serviceProvider";
import {
  MedicineInterface,
  ProductInterface,
  ProductType,
  ServiceProviderRate,
  SpecialLabCategories,
  TaxationInterface
} from "../../interfaces/ProductInterface";
import Can from "../Policy/Can";
import { IThunkDispatch, RootState } from "../../store";
import { getStockProducts } from "../../actions/stockProductActions";
import useCan from "../../hooks/useCan";
import { findByCode } from "../accounts/hooks";
import { ServiceCodes } from "../../interfaces/Accounts";
import { getTaxationOptions } from "../Stock/StockCreateEdit/CreateStockProduct";
import { Test } from "../../interfaces/Lab";
import useTaxations from "../../hooks/useTaxations";
import { getCategory, specialLabCategories } from "./ServiceList";
import { commonErrorMessage } from "../../helpers/messages";
import DepartmentSelect from "../../components/DepartmentSelect";
import { ServiceActions } from "../../interfaces/ActionTypes";
import { SSF_SERVICE_ORIGIN } from ".";
import useDepartmentSubscription from "../../hooks/useDepartmentSubscription";

// @ts-expect-error - css
import "./ServiceList.scss";

// types and interface
interface PropType {
  actions: {
    goBack: (
      isPublicTab: boolean,
      isSsfTab: boolean,
      productId?: number,
      packgeId?: number
    ) => void;
    loadServiceProviders: (id: number) => void;
    navigateTo: (url: string) => void;
    createService: ({
      data,
      createNewAfter,
      isPublicTab,
      isOpd,
      onSuccess
    }: {
      data: Partial<MedicineInterface | ProductInterface>;
      createNewAfter: boolean;
      isPublicTab: boolean;
      isOpd?: boolean;
      onSuccess: () => void;
    }) => void;
    updateService: ({
      data,
      isPublicTab,
      redirectAfterUpdate,
      isSsfTab
    }: {
      data: Partial<MedicineInterface | ProductInterface> & { syncRatesInPackage: boolean };
      isPublicTab: boolean;
      redirectAfterUpdate: boolean;
      isSsfTab: boolean;
    }) => void;
    updateServiceUnderPackage: ({
      data,
      packageId,
      isPublicTab,
      redirectAfterUpdate
    }: {
      data: Partial<MedicineInterface | ProductInterface>;
      isPublicTab: boolean;
      redirectAfterUpdate: boolean;
      packageId: string;
    }) => void;
    getService: (id: number) => void;
    loadServices: () => void;
    loadStockProducts: (query) => void;
    getLabTests: () => void;
    createSuccessNotification: () => void;
    goToStockServices: () => void;
  };
  resourceCentreId: number;
  serviceProviders: [];
  productId?: number;
  product: ProductInterface & { salesLedgerId: number };
  labTests: Array<Test>;
  handleClose: () => void;
  location: { state: { filter: string } };
  isAccount?: boolean;
  isPublicTab?: boolean;
  isOpd?: boolean;
  serviceName?: string;
  redirectAfterUpdate?: boolean;
  productUnderPackage?: any;
  isForEditingServiceUnderPackage?: boolean;
  packageId?: number;
  setActivePackageProduct?: React.Dispatch<React.SetStateAction<any>>;
  isSsfTab?: boolean;
}

export interface StateProductinfo {
  publicBooking: boolean;
  productName: string;
  productPrice: number;
  vatPct: number;
  servicePriceExVAT: number;
  offerPrice: number;
  productUnits: string;
  productType: ProductType | SpecialLabCategories;
  isTaxable: false;
  materialCharge: number;
  serviceProviderRateUnit: string;
  serviceProviderId?: number | null;
  info?: MedicineInterface;
  serviceCode: string;
  isMultiSession?: boolean;
  productisableId?: number | null;
  productisableName?: "labTestGroup" | "medicine" | "";
  labCharge?: number;
  salesLedgerId?: number | null;
  salesTaxationId?: number;
  specialLabTest?: string;
  departmentId?: number;
  publicDescription?: string;
  instructions?: string;
  handledByOkhati?: boolean;
  assignedRc?: number;
  document: {
    rates: Array<ServiceProviderRate | { serviceProviderId: string; rate: string }>;
  };
  origin: string | null;
  subType?: string;
  serviceDeliveryMode?: string;
}

// helper functions
export const calculateServicePriceExVAT = (
  priceIncTax: number | string,
  taxRate: string | number
): string => {
  const priceNum = Number(priceIncTax) || 0;
  const rateNum = Number(taxRate) || 0;
  const result = round(priceNum / (1 + rateNum / 100), 2) as number;
  return String(result);
};

export const findServicePriceExVat = (
  updatedKeys: string[],
  productData: StateProductinfo,
  updates: Record<string, any>,
  isAccountSubscribed: boolean,
  vatRuleOptions: TaxationInterface[]
): string => {
  if (updatedKeys.includes("vatPct") || updatedKeys.includes("salesTaxationId")) {
    if (isAccountSubscribed) {
      if (!updates.salesTaxationId) {
        return calculateServicePriceExVAT(productData.productPrice, 0);
      }
      const selectedVatRule = vatRuleOptions.find((rule) => rule.id === updates.salesTaxationId);
      return calculateServicePriceExVAT(productData.productPrice, selectedVatRule?.rate || 0);
    }

    return calculateServicePriceExVAT(productData.productPrice, updates.vatPct || 0);
  }
  return calculateServicePriceExVAT(updates.productPrice, productData.vatPct || 0);
};

const usedSP = (productData: StateProductinfo) =>
  productData.document?.rates?.map((rate) => rate.serviceProviderId).filter((id) => Boolean(id));

// component
const CreateProduct: React.FC<PropType> = (props) => {
  const {
    resourceCentreId,
    serviceProviders,
    actions,
    productId,
    product,
    labTests,
    handleClose,
    isAccount = false,
    isPublicTab = false,
    isOpd = false,
    serviceName = "",
    redirectAfterUpdate = true,
    productUnderPackage,
    packageId,
    isForEditingServiceUnderPackage = false,
    setActivePackageProduct,
    isSsfTab = false,
    location
  } = props;

  // hooks
  const taxations = useTaxations();
  const showAccountLedger = useCan("account:listAccount", {});
  const { isDepartmentSubscribed } = useDepartmentSubscription();

  // selectors
  const isAccountSubscribed = useSelector(
    (state: RootState) => state.subscriptions.currentSubscription?.features?.account?.subscribed
  );

  // local states
  const [saving, setSaving] = React.useState(false);
  const [title, setTitle] = React.useState<React.ReactNode>(null);
  const [createNewAfter, setCreateNewAfter] = React.useState(false);
  const [showDepartmentField, setShowDepartmentField] = React.useState(false);
  const [isServiceProviderSingle, setisServiceProviderSingle] = React.useState(false);
  const [syncRatesInPackage, setSyncRatesInPackage] = React.useState(false);
  const [productData, setProductData] = React.useState<StateProductinfo>({
    productName: serviceName || "",
    productPrice: 0,
    vatPct: 0,
    servicePriceExVAT: 0,
    productUnits: "pcs",
    offerPrice: 0,
    productType: isOpd ? ProductType.labTest : ProductType.consultation,
    isTaxable: false,
    publicBooking: false,
    serviceCode: "",
    materialCharge: 0,
    isMultiSession: false,
    serviceProviderRateUnit: "percentage",
    serviceProviderId: null,
    productisableId: null,
    productisableName: "",
    labCharge: 0,
    salesLedgerId: null,
    publicDescription: "",
    instructions: "",
    specialLabTest: "",
    handledByOkhati: false,
    document: {
      rates: []
    },
    origin: null,
    subType: "",
    serviceDeliveryMode: ""
  });

  // memo
  const usedServiceProviders = React.useMemo(() => usedSP(productData), [productData]);

  // derived values
  const serviceLedger = findByCode(ServiceCodes.OTHER_INCOME);
  const { salesTaxationOptions } = getTaxationOptions(taxations);

  // helper functions
  const panelHeader = () => {
    if (productId) {
      return setTitle(t("services.editService"));
    }

    if (isForEditingServiceUnderPackage) {
      return setTitle(t("services.editServiceUnderPackage"));
    }

    return setTitle(t("services.createService"));
  };

  const createNewAfterToggler = () => {
    setCreateNewAfter(!createNewAfter);
  };

  const resetForm = () => {
    setSyncRatesInPackage(false);
    setProductData({
      publicBooking: false,
      productName: "",
      productPrice: 0,
      vatPct: 0,
      servicePriceExVAT: 0,
      offerPrice: 0,
      productUnits: "pcs",
      isMultiSession: false,
      productType: createNewAfter ? productData.productType : ProductType.consultation,
      isTaxable: false,
      materialCharge: 0,
      serviceProviderRateUnit: "percentage",
      serviceProviderId: null,
      productisableId: null,
      productisableName: "",
      labCharge: 0,
      salesLedgerId: serviceLedger && isAccountSubscribed ? serviceLedger.id : null,
      serviceCode: "",
      publicDescription: "",
      instructions: "",
      handledByOkhati: false,
      document: {
        rates: []
      },
      origin: null,
      subType: "",
      serviceDeliveryMode: ""
    });
  };

  const onSuccess = () => {
    resetForm();
    actions.loadServices();
  };

  const addAnotherServiceProviderField = () => {
    const newSP = { serviceProviderId: "", rate: "" };
    setProductData({
      ...productData,
      document: {
        rates: [...productData.document.rates, newSP]
      }
    });
  };

  const saveProduct = async () => {
    setSaving(true);
    let newRates: Array<unknown> = [];
    if (productData.document.rates.length > 0) {
      newRates = productData.document.rates.filter(
        (item) => item.serviceProviderId !== "" && item.rate !== ""
      );
    } else {
      newRates = [];
    }

    const serviceData = {
      name: productData.productName,
      publicBooking: productData.publicBooking,
      grossTotalPrice: productData.productPrice,
      vatPct: Number(productData.vatPct) || 0,
      servicePriceExcVAT: Number(productData.servicePriceExVAT),
      unit: productData.productUnits,
      productType: "single",
      offerPrice: productData.offerPrice || 0,
      isMultiSession: productData.isMultiSession,
      serviceCode: productData.serviceCode,
      category: productData.productType,
      materialCharge: productData.materialCharge,
      serviceProviderRateUnit: productData.serviceProviderRateUnit,
      serviceProviderId: productData.serviceProviderId,
      info: productData.info || undefined,
      productisableId: productData.productisableId,
      productisableName: productData.productisableName,
      labCharge: productData.labCharge,
      voucherAssociation: "sales",
      salesLedgerId: productData.salesLedgerId,
      salesTaxationId: productData.salesTaxationId,
      intangible: true,
      departmentId: productData.departmentId,
      publicDescription: productData.publicDescription || "",
      instructions: productData.instructions || "",
      specialLabTest: productData.specialLabTest,
      handledByOkhati: productData.handledByOkhati,
      document: {
        rates: [...newRates]
      },
      subType: productData.subType,
      serviceDeliveryMode: productData.serviceDeliveryMode
    };

    // if intention is to update product under package
    if (isForEditingServiceUnderPackage && packageId) {
      await actions.updateServiceUnderPackage({
        data: { ...serviceData, id: productUnderPackage.id },
        packageId,
        redirectAfterUpdate,
        isPublicTab,
        isSsfTab
      });

      setSaving(false);
      if (handleClose) {
        handleClose();
      }
      return;
    }

    if (productId) {
      if (isPublicTab && !product?.resourceCentreId) {
        await actions.createService({
          data: { ...serviceData, referenceId: productId },
          createNewAfter,
          isPublicTab,
          onSuccess,
          isSsfTab
        });
      } else {
        await actions.updateService({
          data: { ...serviceData, id: productId, syncRatesInPackage },
          isPublicTab,
          redirectAfterUpdate,
          isSsfTab
        });
        if (handleClose) {
          handleClose();
        }
      }
    } else {
      await actions.createService({
        data: serviceData,
        createNewAfter,
        isPublicTab,
        isOpd,
        onSuccess,
        isSsfTab
      });
      if (isOpd && handleClose) {
        handleClose();
      }
    }

    setSaving(false);
  };

  // Effects
  React.useEffect(() => {
    if (serviceLedger && isAccountSubscribed) {
      setProductData({ ...productData, salesLedgerId: serviceLedger.id });
    }
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [isAccountSubscribed, serviceLedger]);

  React.useEffect(() => {
    actions.loadServiceProviders(resourceCentreId);
    panelHeader();
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, []);

  React.useEffect(() => {
    if (
      productData.productType === ProductType.labTest ||
      productData.productType === ProductType.medicalTest
    ) {
      actions.getLabTests();
    }
  }, [productData.productType, actions]);

  React.useEffect(() => {
    if (window.location) {
      if (queryString.parse(window.location.search).createAnotherAfterSave) {
        setCreateNewAfter(true);
      } else {
        setCreateNewAfter(false);
      }
    }
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [window.location]);

  React.useEffect(() => {
    if (productId && !isAccount) {
      actions.getService(productId);
    }
  }, [productId, isAccount, actions]);

  React.useEffect(() => {
    if (location?.state) {
      const { filter } = location.state;
      if (Object.values(ProductType).includes(filter as ProductType)) {
        setProductData({ ...productData, productType: filter as ProductType });
      }
    }
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [location]);

  React.useEffect(() => {
    if (productId && product) {
      setProductData({
        publicBooking: product.publicBooking,
        productName: product.name,
        productPrice: product.grossTotalPrice ? product.grossTotalPrice : 0,
        vatPct: product.vatPct ? product.vatPct : 0,
        offerPrice: product.offerPrice || 0,
        servicePriceExVAT:
          Number(product.vatPct) !== 0 ? product.servicePriceExcVAT : product.grossTotalPrice || 0,
        productUnits: product.unit,
        productType: getCategory(product.category),
        isTaxable: false,
        serviceCode: product?.serviceCode || "",
        materialCharge: product.materialCharge ? product.materialCharge : 0,
        serviceProviderRateUnit: product.serviceProviderRateUnit
          ? product.serviceProviderRateUnit
          : "percentage",
        serviceProviderId: product.serviceProviderId,
        info: product.info,
        productisableId: product.productisableId,
        productisableName: product.productisableName,
        labCharge: product.labCharge || 0,
        salesLedgerId: product.salesLedgerId || null,
        salesTaxationId: product.salesTaxationId || undefined,
        departmentId: product.departmentId,
        publicDescription: product.publicDescription,
        isMultiSession: product.isMultiSession,
        instructions: product.instructions,
        specialLabTest: specialLabCategories.includes(product.category) ? product.category : "",
        handledByOkhati: product.handledByOkhati,
        document: {
          ...product?.document,
          rates: product?.document?.rates || []
        },
        origin: product?.origin || null,
        subType: product?.subType || "",
        serviceDeliveryMode: product?.serviceDeliveryMode || ""
      });
      setisServiceProviderSingle(Boolean(product.serviceProviderId));
    }
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [productId]);

  React.useEffect(() => {
    if (productUnderPackage && isForEditingServiceUnderPackage) {
      setProductData({
        publicBooking: productUnderPackage.publicBooking,
        productName: productUnderPackage.name,
        productPrice: productUnderPackage.grossTotalPrice ? productUnderPackage.grossTotalPrice : 0,
        vatPct: productUnderPackage.vatPct ? productUnderPackage.vatPct : 0,
        servicePriceExVAT:
          Number(productUnderPackage.vatPct) !== 0
            ? productUnderPackage.servicePriceExcVAT
            : productUnderPackage.grossTotalPrice || 0,
        productUnits: productUnderPackage.unit,
        productType: getCategory(productUnderPackage.category),
        isTaxable: false,
        serviceCode: productUnderPackage.serviceCode,
        materialCharge: productUnderPackage.materialCharge ? productUnderPackage.materialCharge : 0,
        serviceProviderRateUnit: productUnderPackage.serviceProviderRateUnit
          ? productUnderPackage.serviceProviderRateUnit
          : "percentage",
        serviceProviderId: productUnderPackage.serviceProviderId,
        info: productUnderPackage.info,
        productisableId: productUnderPackage.productisableId,
        productisableName: productUnderPackage.productisableName,
        labCharge: productUnderPackage.labCharge || 0,
        salesLedgerId: productUnderPackage.salesLedgerId || null,
        salesTaxationId: productUnderPackage.salesTaxationId || null,
        departmentId: productUnderPackage.departmentId,
        publicDescription: productUnderPackage.publicDescription,
        instructions: productUnderPackage.instructions,
        specialLabTest: specialLabCategories.includes(productUnderPackage.category)
          ? productUnderPackage.category
          : "",
        handledByOkhati: productUnderPackage.handledByOkhati,
        document: {
          rates: productUnderPackage.document.rates || []
        },
        origin: productUnderPackage.origin || null,
        subType: productUnderPackage.subType || "",
        serviceDeliveryMode: productUnderPackage?.serviceDeliveryMode || ""
      });
      setisServiceProviderSingle(Boolean(productUnderPackage.serviceProviderId));
    }
  }, [isForEditingServiceUnderPackage, productUnderPackage]);

  return (
    <Can policyAccessKey="services:createProduct">
      <Panel
        title={title}
        onClose={() => {
          if (handleClose) {
            handleClose();
          }
          if (setActivePackageProduct) {
            setActivePackageProduct(null);
          }
          if (!isAccount && !isOpd) {
            actions.goBack(isPublicTab, isSsfTab, productId, packageId);
          }
        }}
        footer={
          <Footer
            editable={!productId}
            saveFn={saveProduct}
            saveDisabled={
              !(
                productData?.productName?.length > 0 &&
                (isAccountSubscribed ? Boolean(productData.salesLedgerId) : true)
              ) ||
              saving ||
              productData?.productPrice?.toString() === "" ||
              (productData?.productType === ProductType.labTest && !productData?.productisableId) ||
              productData.serviceCode?.length
                ? productData.serviceCode?.length < 3
                : false
            }
            importing={isPublicTab && !product?.resourceCentreId && product?.publicBooking}
            goBack={() => {
              if (handleClose) {
                handleClose();
              }
              if (setActivePackageProduct) {
                setActivePackageProduct(null);
              }
              actions.goBack(isPublicTab, isSsfTab, productId, packageId);
            }}
            createNewAfter={createNewAfter}
            createNewAfterToggler={createNewAfterToggler}
            isAccount={isAccount}
          />
        }
      >
        <Box style={{ width: "100%" }}>
          <CommonServiceFields
            productId={productId}
            isAccount={isAccount}
            isOpd={isOpd}
            showAccountLedger={showAccountLedger}
            labTests={labTests}
            fieldData={productData}
            isPublicTab={isPublicTab}
            isForEditingServiceUnderPackage={isForEditingServiceUnderPackage}
            isSsfService={productData.origin === SSF_SERVICE_ORIGIN || isSsfTab}
            onUpdate={(updates: Record<string, unknown> = {}) => {
              if (typeof updates === "string") return;
              const updatedKeys = Object.keys(updates);

              if (
                updatedKeys.includes("vatPct") ||
                updatedKeys.includes("productPrice") ||
                updatedKeys.includes("salesTaxationId")
              ) {
                const mutatedUpdates = produce(updates, (draft: any) => {
                  // NOTE: VAT logic can be removed from here in the future
                  draft.servicePriceExVAT = findServicePriceExVat(
                    updatedKeys,
                    productData,
                    updates,
                    isAccountSubscribed,
                    salesTaxationOptions
                  );
                });
                setProductData({ ...productData, ...mutatedUpdates });
              } else {
                setProductData({ ...productData, ...updates });
              }
            }}
          />

          {isPublicTab && (
            <FormControlLabel
              control={
                <Checkbox
                  checked={productData.publicBooking}
                  onChange={() =>
                    setProductData({ ...productData, publicBooking: !productData.publicBooking })
                  }
                />
              }
              label="Public Booking"
            />
          )}

          <Can policyAccessKey="services:addPayables">
            <Box>
              {productData.document?.rates?.length > 0 ? (
                <>
                  <Typography style={{ marginBottom: "16px" }}>
                    <Box component="span" fontWeight="600">
                      {tl("services.serviceProviderPayableRates")}
                    </Box>
                  </Typography>
                  <Box
                    width="100%"
                    display="flex"
                    justifyContent="space-between"
                    borderBottom="0.5px solid lightgray"
                    pb={1}
                  >
                    {" "}
                    <Box width="50%">
                      <Typography>
                        <Box component="span" fontWeight="500">
                          {tl("serviceProvider")}
                        </Box>
                      </Typography>
                    </Box>
                    <Box width="20%" />
                    <Box
                      width="20%"
                      display="flex"
                      alignItems="center"
                      justifyContent="space-between"
                    >
                      <Typography>
                        <Box component="span" fontWeight="500" pr={2}>
                          {tl("rate")}
                        </Box>
                      </Typography>
                      <ButtonGroup size="small" variant="outlined">
                        <Button
                          data-testmation="serviceProviderRateUnitRupee"
                          size="small"
                          variant="outlined"
                          onClick={() => {
                            setProductData({
                              ...productData,
                              serviceProviderRateUnit: "rupee"
                            });
                          }}
                        >
                          <Typography>
                            <Box component="span">{tl("rs")}</Box>
                          </Typography>
                        </Button>
                        <Button
                          data-testmation="serviceProviderRateUnitPercentage"
                          size="small"
                          variant="outlined"
                          onClick={() => {
                            setProductData({
                              ...productData,
                              serviceProviderRateUnit: "percentage"
                            });
                          }}
                        >
                          <Typography>%</Typography>
                        </Button>
                      </ButtonGroup>
                    </Box>
                    <Box width="10%" />
                  </Box>

                  {productData?.document?.rates.map((rate, index) => (
                    // eslint-disable-next-line react/no-array-index-key
                    <React.Fragment key={index}>
                      <RateRow
                        serviceProviders={serviceProviders}
                        usedServiceProviders={usedServiceProviders}
                        rate={rate}
                        onChange={(newRate) => {
                          const newProductData = cloneDeep(productData);
                          newProductData.document.rates[index] = newRate;
                          if (!newRate.serviceProviderId) {
                            newProductData.document.rates[index].serviceProviderId = null;
                          }
                          setProductData(newProductData);
                        }}
                        onRemove={() => {
                          const newProductData = cloneDeep(productData);
                          newProductData.document.rates.splice(index, 1);
                          setProductData(newProductData);
                        }}
                        serviceProviderRateUnit={productData.serviceProviderRateUnit}
                      />
                    </React.Fragment>
                  ))}

                  <Box
                    sx={{
                      mt: 1,
                      display: "flex",
                      flexDirection: "column",
                      alignItems: "start",
                      gap: 1
                    }}
                  >
                    {/* ask if this service belongs to only one sp */}
                    {productData.document?.rates?.length === 1 &&
                      productData.document?.rates[0] &&
                      Number(productData.document?.rates[0]?.serviceProviderId ?? 0) !== 0 && (
                        <Box display="flex" alignItems="center">
                          <Checkbox
                            checked={isServiceProviderSingle}
                            onChange={(e) => {
                              setisServiceProviderSingle(!isServiceProviderSingle);
                              if (e.target.checked) {
                                setProductData({
                                  ...productData,
                                  serviceProviderId: Number(
                                    productData.document.rates[0].serviceProviderId
                                  )
                                });
                              } else {
                                setProductData({
                                  ...productData,
                                  serviceProviderId: null
                                });
                              }
                            }}
                            sx={{ marginLeft: "-8px" }}
                          />
                          <Typography>
                            This product is for {"  "}
                            {spFullNameSelector(
                              spFromIdSelector(
                                serviceProviders,
                                productData.document?.rates[0]?.serviceProviderId
                              )
                            )}
                            {"  "}
                            only
                          </Typography>
                        </Box>
                      )}

                    {/* ask if rate should be sysnced in the packages? */}
                    {productId && product && (
                      <Box sx={{ display: "flex", alignItems: "center" }}>
                        <Checkbox
                          checked={syncRatesInPackage}
                          onChange={(e) => setSyncRatesInPackage(e.target.checked)}
                          sx={{ marginLeft: "-8px", py: 0 }}
                        />
                        <Typography variant="body2" sx={{ mr: 1 }}>
                          Update this service&apos;s doctor commission rates in every package that
                          includes it.
                        </Typography>
                        <Tooltip
                          title="Existing doctor's commission rates will be updated, and
                          commission rates for new doctors will be added. Doctors whose commission
                          rates are removed from this service will not be removed from packages. 
                          All of these will be carried out only if this service is included in a package and the sp's rate unit matches"
                        >
                          <InfoIcon />
                        </Tooltip>
                      </Box>
                    )}

                    {!isServiceProviderSingle && (
                      <Button
                        data-testmation="productCreateAddAnotherServiceProviderField"
                        onClick={() => addAnotherServiceProviderField()}
                        variant="outlined"
                        size="small"
                      >
                        {tl("services.addAnother")}
                      </Button>
                    )}
                  </Box>
                </>
              ) : (
                <Button
                  data-testmation="productCreateAddServiceProviderPayableRates"
                  onClick={() => addAnotherServiceProviderField()}
                  variant="outlined"
                  size="small"
                >
                  {tl("services.addServiceProviderPayableRates")}
                </Button>
              )}
            </Box>
          </Can>

          <Box sx={{ mt: 1 }}>
            {!showDepartmentField && !productData.departmentId && isDepartmentSubscribed && (
              <Button variant="outlined" size="small" onClick={() => setShowDepartmentField(true)}>
                {tl("service.create.addDepartment")}
              </Button>
            )}

            {(showDepartmentField || productData?.departmentId) && (
              <Box display="flex" alignItems="center" mt={2}>
                <DepartmentSelect
                  placeholderText="Select department"
                  style={{ width: "310px", marginTop: "8px" }}
                  id={productData.departmentId}
                  onChange={(department) =>
                    setProductData({ ...productData, departmentId: department.id })
                  }
                />
                <Close
                  onClick={() => setShowDepartmentField(false)}
                  sx={{ cursor: "pointer", marginLeft: "15px" }}
                />
              </Box>
            )}
          </Box>
        </Box>
      </Panel>
    </Can>
  );
};

function mapStateToProps(state: RootState, ownProps: PropType) {
  let product =
    [
      ...(ownProps.isPublicTab ? state?.publicServices?.collection : []),
      ...state.services.collection
    ].find((item) => ownProps.productId === item.id) || {};

  // NOTE: isAccount is main passed for the components related to stock.
  // Why are we even using this component in stock related components?
  if (ownProps.isAccount && ownProps.productId) {
    const data = state.stockProducts.collection.find((item) => ownProps.productId === item.id);
    product = {
      name: data.name,
      grossTotalPrice: data.unitPriceIncVAT || 0,
      vatPct: data.vatPct || 0,
      servicePriceExcVAT: data.unitPriceExcVAT || 0,
      unit: data.unit,
      category: data.category,
      offerPrice: data.offerPrice || 0,
      isMultiSession: data.isMultiSession,
      serviceCode: data.serviceCode,
      materialCharge: data.serviceDetails?.materialCharge || 0,
      serviceProviderRateUnit: data.serviceDetails?.serviceProviderRateUnit,
      serviceProviderId: data.serviceDetails?.serviceProviderId,
      info: undefined,
      productisableId: data.serviceDetails?.productisableId,
      productisableName: data.serviceDetails?.productisableName,
      salesLedgerId: data.salesLedgerId || null,
      labCharge: data.serviceDetails?.labCharge,
      document: {
        ...(data.document?.rates ? { rates: [...data.document.rates] } : {}),
        ...(data.document?.products ? { products: [...data.document.products] } : {})
      }
    };
  }
  const serviceProviders = serviceProvidersSortedSelector(state);
  const { resourceCentreId } = state.userContext;
  const { labTests } = state.assessments;
  let productServiceProviders = [];
  if (product && serviceProviders?.length) {
    productServiceProviders = serviceProviders?.filter((sp) => {
      const productSP = product?.document?.rates?.map((rate) => rate.serviceProviderId);
      if (productSP?.includes(sp.id)) return sp;
      return false;
    });
    const productServiceProvidersIds = productServiceProviders?.map((sp) => sp.id);
    product = {
      ...product,
      document: {
        rates: product?.document?.rates?.filter((rate) => {
          if (productServiceProvidersIds?.includes(rate.serviceProviderId)) {
            return rate;
          }
          return false;
        })
      }
    };
  }

  return {
    serviceProviders,
    resourceCentreId,
    product,
    labTests
  };
}

export default connect(mapStateToProps, (dispatch: IThunkDispatch) => ({
  actions: {
    goBack: (isPublicTab, isSsfTab, productId, packageId) => {
      let basePath = "/services";
      if (isPublicTab) {
        basePath = "/publicServices";
      } else if (isSsfTab) {
        basePath = "/socialSecurityFundServices";
      }

      if (productId) {
        dispatch(push(`${basePath}/product/${productId}`));
      } else if (packageId) {
        dispatch(push(`${basePath}/package/${packageId}`));
      } else {
        dispatch(push(basePath));
      }
    },
    goToStockServices: () => dispatch(push("/stock/stockServices")),
    navigateTo: (url) => {
      dispatch(push(url));
    },
    loadServiceProviders: (id) =>
      dispatch(
        serviceProviderActions.getResourceCentreServiceProviders({
          resourceCentreId: id
        })
      ),
    createSuccessNotification: () =>
      dispatch(
        NotificationActions.notificationAdd({
          id: new Date().getUTCMilliseconds(),
          variant: "success",
          message: "Completed Successfully",
          autoTimeout: true
        })
      ),
    createService: async ({ data, createNewAfter, isPublicTab, isOpd, onSuccess, isSsfTab }) => {
      await dispatch(async (dispatchInner, getState) => {
        await dispatchInner(createServices(data));
        const currentState = getState();
        const errorObject = currentState.error.filter(({ key }) => key === "CREATE_SERVICES");
        let rootPath = isPublicTab ? "/publicServices" : "/services";
        if (isSsfTab) {
          rootPath = "/socialSecurityFundServices";
        }
        if (errorObject.length) {
          dispatchInner(
            NotificationActions.notificationAdd({
              id: new Date().getUTCMilliseconds(),
              variant: "error",
              message: errorObject[0].message || commonErrorMessage,
              autoTimeout: true
            })
          );
          dispatchInner(removeGeneralErrorByKey("CREATE_SERVICES"));
        } else {
          dispatchInner(
            NotificationActions.notificationAdd({
              id: new Date().getUTCMilliseconds(),
              variant: "success",
              message: tl("services.successProductCreation"),
              autoTimeout: true
            })
          );
          onSuccess();
          if (isOpd) return;
          if (createNewAfter) {
            dispatchInner(
              push({
                pathName: `${rootPath}/newProduct/?createNewAfterSave=${createNewAfter}`,
                state: { filter: currentState.services.lastTouched.category }
              } as Record<string, unknown>)
            );
          } else {
            dispatchInner(push(`${rootPath}/product/${currentState.services.lastTouched.id}`));
          }
        }
      });
    },
    updateServiceUnderPackage: async ({
      data,
      packageId,
      redirectAfterUpdate,
      isPublicTab,
      isSsfTab
    }) => {
      dispatch(async (dispatchInner, getState) => {
        await dispatchInner(updateServiceUnderPackage({ data, packageId }));
        await dispatchInner(getService(packageId));
        const currentState = getState();
        let rootPath = isPublicTab ? "/publicServices" : "/services";
        if (isSsfTab) {
          rootPath = "/socialSecurityFundServices";
        }
        const errorObject = currentState.error.filter(
          ({ key }) => key === ServiceActions.UPDATE_SERVICE_UNDER_PACKAGE
        );
        if (errorObject.length) {
          dispatchInner(
            NotificationActions.notificationAdd({
              id: new Date().getUTCMilliseconds(),
              variant: "error",
              message: errorObject[0].message || commonErrorMessage,
              autoTimeout: true
            })
          );
          dispatchInner(removeGeneralErrorByKey(ServiceActions.UPDATE_SERVICE_UNDER_PACKAGE));
        } else {
          dispatchInner(
            NotificationActions.notificationAdd({
              id: new Date().getUTCMilliseconds(),
              variant: "success",
              message: tl("services.successUpdateProduct"),
              autoTimeout: true
            })
          );

          if (redirectAfterUpdate) {
            dispatchInner(push(`${rootPath}/package/${packageId}`));
          }
        }
      });
    },
    updateService: async ({ data, isPublicTab, redirectAfterUpdate, isSsfTab }) => {
      await dispatch(async (dispatchInner, getState) => {
        await dispatchInner(updateServices(data));
        let rootPath = isPublicTab ? "/publicServices" : "/services";
        if (isSsfTab) {
          rootPath = "/socialSecurityFundServices";
        }

        const currentState = getState();
        const errorObject = currentState.error.filter(({ key }) => key === "UPDATE_SERVICES");
        if (errorObject.length) {
          dispatchInner(
            NotificationActions.notificationAdd({
              id: new Date().getUTCMilliseconds(),
              variant: "error",
              message: errorObject[0].message || commonErrorMessage,
              autoTimeout: true
            })
          );
          dispatchInner(removeGeneralErrorByKey("UPDATE_SERVICES"));
        } else {
          dispatchInner(
            NotificationActions.notificationAdd({
              id: new Date().getUTCMilliseconds(),
              variant: "success",
              message: tl("services.successUpdateProduct"),
              autoTimeout: true
            })
          );
          if (redirectAfterUpdate) {
            dispatchInner(push(`${rootPath}/product/${currentState.services.lastTouched.id}`));
          }
        }
      });
    },
    getService: (id) => dispatch(getService(id)),
    loadServices: () => dispatch(getServices()),
    loadStockProducts: (query) => dispatch(getStockProducts(query)),
    getLabTests: () => dispatch(getBatchLabTests(true))
  }
}))(CreateProduct);
